import { useState } from 'react';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT_IMAGE = "https://images.unsplash.com/photo-1622659097972-68f1d8c1829f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHx5b3V0aCUyMHNwb3J0cyUyMGtpZHMlMjBwbGF5aW5nJTIwc29jY2VyfGVufDB8fHx8MTc3NDk4Nzg1M3ww&ixlib=rb-4.1.0&q=85";

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ type: 'success', message: 'Thank you for reaching out! We\'ll be in touch soon.' });
      setFormData({ name: '', email: '', organization: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form Side */}
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
                Contact Us
              </p>
              <h1 className="text-4xl sm:text-5xl tracking-tight font-extrabold text-[#272926] leading-tight mb-6">
                Let's connect
              </h1>
              <p className="text-lg leading-relaxed text-zinc-600 mb-10">
                Have questions or want to learn more about our work? We'd love to hear from you.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-[#272926] font-semibold mb-2 block">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="w-full rounded-xl border-zinc-200 focus:ring-[#5B4FF9] focus:border-[#5B4FF9]"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#272926] font-semibold mb-2 block">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="w-full rounded-xl border-zinc-200 focus:ring-[#5B4FF9] focus:border-[#5B4FF9]"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="organization" className="text-[#272926] font-semibold mb-2 block">
                    Organization
                  </Label>
                  <Input
                    id="organization"
                    name="organization"
                    type="text"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder="Your organization (optional)"
                    className="w-full rounded-xl border-zinc-200 focus:ring-[#5B4FF9] focus:border-[#5B4FF9]"
                    data-testid="contact-org-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#272926] font-semibold mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    required
                    rows={5}
                    className="w-full rounded-xl border-zinc-200 focus:ring-[#5B4FF9] focus:border-[#5B4FF9] resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#5B4FF9] hover:bg-[#4A3FE8] text-white rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg"
                  data-testid="contact-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>

                {status.message && (
                  <div 
                    className={`flex items-center gap-2 p-4 rounded-xl ${
                      status.type === 'success' 
                        ? 'bg-green-50 text-green-700' 
                        : 'bg-red-50 text-red-700'
                    }`}
                    data-testid="contact-status"
                  >
                    {status.type === 'success' ? (
                      <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    )}
                    <span>{status.message}</span>
                  </div>
                )}
              </form>
            </div>

            {/* Image Side */}
            <div className="hidden lg:block relative">
              <img 
                src={CONTACT_IMAGE} 
                alt="Youth soccer players"
                className="rounded-3xl shadow-xl w-full h-full object-cover"
              />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#5B4FF9] rounded-2xl -z-10"></div>
              
              {/* Contact Info Card */}
              <div className="absolute bottom-8 right-8 bg-white rounded-2xl p-6 shadow-xl max-w-xs">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-[#F5F4FF] rounded-full flex items-center justify-center mr-3">
                    <Mail className="h-5 w-5 text-[#5B4FF9]" />
                  </div>
                  <span className="font-semibold text-[#272926]">Email Us</span>
                </div>
                <a 
                  href="mailto:foundation@playershealth.com"
                  className="text-[#5B4FF9] hover:text-[#4A3FE8] transition-colors font-medium"
                  data-testid="contact-email-link"
                >
                  foundation@playershealth.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
